import type {
  OnboardingAiTool,
  OnboardingAiUsagePattern,
  OnboardingExperienceLevel,
  OnboardingGoal,
  OnboardingWeakArea,
} from './schemas/onboarding';
import {
  onboardingAiToolSchema,
  onboardingAiUsagePatternSchema,
  onboardingExperienceLevelSchema,
  onboardingGoalSchema,
  onboardingWeakAreaSchema,
} from './schemas/onboarding';

export type OnboardingOption<T extends string> = {
  value: T;
  label: string;
};

export const ONBOARDING_EXPERIENCE_OPTIONS: OnboardingOption<OnboardingExperienceLevel>[] = [
  { value: 'student', label: 'Student' },
  { value: 'bootcamp', label: 'Bootcamp graduate' },
  { value: 'self_taught', label: 'Self-taught' },
  { value: 'junior', label: 'Junior developer' },
];

export const ONBOARDING_GOAL_OPTIONS: OnboardingOption<OnboardingGoal>[] = [
  { value: 'first_job', label: 'Land my first job' },
  { value: 'internship', label: 'Get an internship' },
  { value: 'junior_interview', label: 'Prepare for junior interviews' },
  { value: 'current_job_growth', label: 'Grow in my current role' },
];

export const ONBOARDING_AI_TOOL_OPTIONS: OnboardingOption<OnboardingAiTool>[] = [
  { value: 'chatgpt', label: 'ChatGPT' },
  { value: 'copilot', label: 'GitHub Copilot' },
  { value: 'cursor', label: 'Cursor' },
  { value: 'claude', label: 'Claude' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'other', label: 'Other' },
];

export const ONBOARDING_USAGE_PATTERN_OPTIONS: OnboardingOption<OnboardingAiUsagePattern>[] = [
  { value: 'copy_answer', label: 'Copy answers without understanding' },
  { value: 'ask_explanation', label: 'Ask for explanations' },
  { value: 'debug_with_ai', label: 'Debug problems with AI' },
  { value: 'generate_tests', label: 'Generate tests or boilerplate' },
];

export const ONBOARDING_WEAK_AREA_OPTIONS: OnboardingOption<OnboardingWeakArea>[] = [
  { value: 'basics', label: 'Programming fundamentals' },
  { value: 'oop', label: 'OOP & classes' },
  { value: 'debugging', label: 'Debugging' },
  { value: 'data_structures', label: 'Data structures' },
  { value: 'web_backend', label: 'Web & backend basics' },
  { value: 'interview_explanation', label: 'Explaining solutions in interviews' },
];

// BR-18: CV, skills review, optional JD (PRE-112), then the four declaration
// steps (experience/goal, 1–6 tools, 1–4 usage patterns, 1–3 weak areas).
export const ONBOARDING_STEP_COUNT = 7;

export const ONBOARDING_STEP_ROUTES = [
  'step-cv',
  'step-skills',
  'step-jd',
  'step-experience-goal',
  'step-ai-tools',
  'step-usage-pattern',
  'step-weak-areas',
] as const;

export type OnboardingStepRoute = (typeof ONBOARDING_STEP_ROUTES)[number];

export function getOnboardingStepIndex(route: OnboardingStepRoute): number {
  return ONBOARDING_STEP_ROUTES.indexOf(route);
}

export function getOnboardingStepRoute(step: number): OnboardingStepRoute | null {
  return ONBOARDING_STEP_ROUTES[step] ?? null;
}

export function getOptionLabel<T extends string>(
  options: OnboardingOption<T>[],
  value: T | undefined,
): string | undefined {
  if (!value) return undefined;
  return options.find((o) => o.value === value)?.label;
}

export const onboardingSchemas = {
  experienceLevel: onboardingExperienceLevelSchema,
  goal: onboardingGoalSchema,
  aiTool: onboardingAiToolSchema,
  aiUsagePattern: onboardingAiUsagePatternSchema,
  weakArea: onboardingWeakAreaSchema,
} as const;
